import Head from 'next/head';
import Link from 'next/link';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import type { GetStaticProps } from 'next';

import WideLayout from '@components/layout/wide';
import PlayerSearch from '@components/player-search';
import { FFH_HOSTNAME } from '@lib/constants';
import {
  fetchClubs,
  fetchLeaguePlayers
} from '@lib/page-utils/player-profile/catalogue';
import {
  buildBreadcrumbJsonLd,
  type BreadcrumbEntry
} from '@lib/page-utils/player-profile/seo';
import {
  buildPlayerSlug,
  PLAYERS_INDEX_PATH,
  toClubIndexPath,
  toPlayerByIdPath,
  toPlayerProfilePath
} from '@lib/page-utils/player-profile/slugs';
import { formatPrice } from '@lib/page-utils/player-profile/format';
import type { Player as FfhPlayer } from '@api-service/players';

const BRAND_NAME = 'Fantasy Football Hub';
const INDEX_REVALIDATE_SECONDS = 3600;
/** Retry window after a failed build-time fetch left the page degraded. */
const DEGRADED_REVALIDATE_SECONDS = 60;
const POPULAR_PLAYER_COUNT = 12;

const PAGE_TITLE = 'FPL player profiles';
const PAGE_DESCRIPTION =
  'FPL stat profiles for every Premier League player: form against expected points, underlying OPTA numbers, upcoming fixtures and gameweek-by-gameweek logs.';

type ClubCard = {
  name: string;
  path: string;
};

type PopularPlayer = {
  displayName: string;
  teamName: string;
  position: string;
  price: number | null;
  path: string;
};

type PlayersIndexProps = {
  clubs: ClubCard[];
  popular: PopularPlayer[];
};

const BREADCRUMBS: BreadcrumbEntry[] = [
  { label: 'Toolbox', path: '/tools' },
  { label: 'Player profiles' }
];

const PlayersIndexPage = ({ clubs, popular }: PlayersIndexProps) => {
  const router = useRouter();
  const canonicalUrl = `${FFH_HOSTNAME}${PLAYERS_INDEX_PATH}`;

  const handlePlayerClick = useCallback(
    (selectedPlayer: FfhPlayer) => {
      const optaId =
        selectedPlayer.externalIds?.optaUuid ?? selectedPlayer.optaId;
      if (optaId) router.push(toPlayerByIdPath(optaId));
    },
    [router]
  );

  return (
    <>
      <Head>
        <title>{`${PAGE_TITLE} - stats for every player | ${BRAND_NAME}`}</title>
        <meta name="description" content={PAGE_DESCRIPTION} key="description" />
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        <meta property="og:type" content="website" key="og:type" />
        <meta property="og:title" content={PAGE_TITLE} key="og:title" />
        <meta
          property="og:description"
          content={PAGE_DESCRIPTION}
          key="og:description"
        />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        <meta property="og:site_name" content={BRAND_NAME} key="og:site_name" />
        <script
          type="application/ld+json"
          key="jsonld-breadcrumbs"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildBreadcrumbJsonLd(BREADCRUMBS))
          }}
        />
      </Head>

      <WideLayout content={false}>
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
          <nav
            aria-label="Breadcrumb"
            className="mb-4 text-xs text-neutral-500"
          >
            <Link href="/tools" className="hover:underline">
              Toolbox
            </Link>
            <span aria-hidden className="mx-2 text-neutral-300">
              /
            </span>
            <span className="text-neutral-800">Player profiles</span>
          </nav>

          <h1 className="font-sans text-3xl font-bold text-neutral-900">
            FPL player profiles
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-600">
            {PAGE_DESCRIPTION}
          </p>

          <div className="mt-5 max-w-md">
            <PlayerSearch
              searchId="players-index-search"
              label="Search for a player"
              onPlayerClick={handlePlayerClick}
              hasPaywall={false}
            />
          </div>

          {popular.length > 0 && (
            <section className="mt-9">
              <h2 className="font-sans text-lg font-bold text-neutral-900">
                Most owned players
              </h2>
              <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {popular.map((player) => (
                  <li key={player.path}>
                    <Link
                      href={player.path}
                      className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3 hover:bg-neutral-50"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-neutral-900">
                          {player.displayName}
                        </span>
                        <span className="block text-xs text-neutral-500">
                          {player.teamName} · {player.position}
                        </span>
                      </span>
                      <span className="text-sm tabular-nums text-neutral-600">
                        {formatPrice(player.price)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-9">
            <h2 className="font-sans text-lg font-bold text-neutral-900">
              Browse by club
            </h2>
            {clubs.length > 0 ? (
              <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {clubs.map((clubCard) => (
                  <li key={clubCard.path}>
                    <Link
                      href={clubCard.path}
                      className="block rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50"
                    >
                      {clubCard.name}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-neutral-500">
                Club pages are refreshing. Use the search above to jump straight
                to a player.
              </p>
            )}
          </section>
        </div>
      </WideLayout>
    </>
  );
};

export const getStaticProps: GetStaticProps<PlayersIndexProps> = async () => {
  let clubEntries: Awaited<ReturnType<typeof fetchClubs>>;
  let leaguePlayers: FfhPlayer[];
  try {
    [clubEntries, leaguePlayers] = await Promise.all([
      fetchClubs(),
      fetchLeaguePlayers()
    ]);
  } catch {
    // This page generates at build time, where the backend may be
    // unreachable (the Cypress CI environment points it at a hostname that
    // only exists once the test runtime is up). Failing here fails the whole
    // build, so the page degrades to its search-only shell and ISR retries
    // shortly rather than serving the empty state for the full hour.
    return {
      props: { clubs: [], popular: [] },
      revalidate: DEGRADED_REVALIDATE_SECONDS
    };
  }

  const clubs = clubEntries.map((entry) => ({
    name: entry.team.name,
    path: toClubIndexPath(entry.clubSlug)
  }));

  const clubSlugByShortName = new Map(
    clubEntries.map((entry) => [entry.team.shortName, entry.clubSlug])
  );

  // Slugs are collision-aware within a club, so the league list is regrouped
  // into squads before any slug is built.
  const squadsByShortName = new Map<string, FfhPlayer[]>();
  for (const player of leaguePlayers) {
    const squad = squadsByShortName.get(player.team.shortName) ?? [];
    squad.push(player);
    squadsByShortName.set(player.team.shortName, squad);
  }

  // The league list arrives ownership-sorted, so the head of it is already
  // the most-owned selection.
  const popular = leaguePlayers
    .slice(0, POPULAR_PLAYER_COUNT)
    .flatMap((player) => {
      const clubSlug = clubSlugByShortName.get(player.team.shortName);
      const squad = squadsByShortName.get(player.team.shortName);
      if (!clubSlug || !squad) return [];

      return [
        {
          displayName: player.displayName,
          teamName: player.team.fullName ?? player.team.shortName,
          position: player.position,
          price: player.price ?? null,
          path: toPlayerProfilePath(clubSlug, buildPlayerSlug(player, squad))
        }
      ];
    });

  return {
    props: { clubs, popular },
    revalidate: INDEX_REVALIDATE_SECONDS
  };
};

export default PlayersIndexPage;
